'use client';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { useCurrentUser, useSession } from '@/hooks';
import { HttpService, UserService } from '@/services';
import type { AuthContextType } from '@/types';
import { toast } from '@/utils/client/toast';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { data: session, isLoading: sessionLoading, isFetched: isSessionLoaded } = useSession();
  const isLoggedIn = !!session?.token && UserService.isAccessTokenValid(session?.token);
  const { data: currentUser, isLoading: currentUserLoading } = useCurrentUser(isLoggedIn);
  const isLoading = sessionLoading || currentUserLoading;

  const logout = () => {
    queryClient.removeQueries({ queryKey: ['session'] });
    queryClient.removeQueries({ queryKey: ['currentUser'] });
    HttpService.unsetAuthorizationHeader();
  };

  useEffect(() => {
    if (!sessionLoading) {
      if (isLoggedIn && currentUser) {
        toast.info(`Willkommen, ${currentUser.firstName}`);
      }
      if (!isLoggedIn) toast.info('Du bist nicht angemeldet');
    }
  }, [currentUser, isLoggedIn, sessionLoading]);

  return (
    <AuthContext.Provider
      value={{
        session,
        currentUser,
        isLoggedIn,
        isLoading,
        isSessionLoaded,
        logout,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
